import { type PublicHostedAuthSession } from '@unipile/provider-shared';
import { type Provider } from '@unipile/types';
import {
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  CardContent,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@unipile/ui';
import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { language_names, locales } from '../../config/locales';
import { MailingProvidersList, MessagingProvidersList, ProviderIcons, ProviderNames } from '../../config/providers';
import i18n from '../../libs/i18n';
import { ProviderWizard } from './ProviderWizard';

export function ProviderSelectionContainer({ session }: { session: PublicHostedAuthSession }) {
  const isOneProvider = session.providers.length === 1;
  const [selected, setSelected] = useState<Provider | null>(isOneProvider ? session.providers[0] : null);

  const handleProviderSelect = (provider: Provider) => {
    setSelected(provider);
  };

  // Show provider wizard (consent is now handled by each provider via useScopesConsent)
  if (selected) {
    return (
      <>
        <div>
          <ProviderWizard provider={selected} />
        </div>
        <Footer session={session} />
      </>
    );
  }

  // Show provider selection
  return (
    <>
      <ProvidersList onSelect={handleProviderSelect} session={session} />
      <Footer session={session} />
    </>
  );
}

function Footer({ session }: { session: PublicHostedAuthSession }) {
  const { t, i18n: i18nInstance } = useTranslation();
  const hasLinks = session.help_url || session.privacy_url || session.terms_url;

  const current_language = i18nInstance.resolvedLanguage || 'en';

  const handleLanguageChange = (locale: string) => {
    i18n.changeLanguage(locale).then(() => {
      // Ensure language is persisted in localStorage
      // (LanguageDetector should do this automatically, but we ensure it explicitly)
      localStorage.setItem('i18nextLng', locale);
    });
  };

  return (
    <div className="mt-2 flex w-full justify-between opacity-50">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            {language_names[current_language] || language_names.en}
            <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {locales.map((locale) => (
            <DropdownMenuItem
              key={locale}
              onClick={() => handleLanguageChange(locale)}
              className={current_language === locale ? 'bg-accent' : ''}
            >
              {language_names[locale]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {hasLinks && (
        <div className="flex">
          {session.help_url && (
            <Button variant="ghost" size="sm" asChild>
              <a href={session.help_url} target="_blank" rel="noopener noreferrer">
                {t('Footer.help')}
              </a>
            </Button>
          )}
          {session.privacy_url && (
            <Button variant="ghost" size="sm" asChild>
              <a href={session.privacy_url} target="_blank" rel="noopener noreferrer">
                {t('Footer.privacy')}
              </a>
            </Button>
          )}
          {session.terms_url && (
            <Button variant="ghost" size="sm" asChild>
              <a href={session.terms_url} target="_blank" rel="noopener noreferrer">
                {t('Footer.terms')}
              </a>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function ProvidersList({
  session: { providers, brand_name, brand_icon_url },
  onSelect,
}: {
  session: PublicHostedAuthSession;
  onSelect: (provider: Provider) => void;
}) {
  const { t } = useTranslation();
  const messagingProviders = providers.filter((p) => MessagingProvidersList.includes(p));
  const mailingProviders = providers.filter((p) => MailingProvidersList.includes(p));

  return (
    <Card className="w-full">
      <CardHeader className="text-center">
        {brand_icon_url && <img alt="icon" src={brand_icon_url} className="mx-auto mb-2 h-10 w-10 object-contain" />}
        <CardTitle>{t('ProvidersList.title')}</CardTitle>
        <CardDescription>
          {brand_name ? (
            <Trans i18nKey="ProvidersList.description" values={{ app_name: brand_name }}>
              to connect an account with <span className="text-primary/70 font-medium">MyApp</span>
            </Trans>
          ) : (
            t('ProvidersList.description_short')
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="mt-4 grid gap-4">
        {messagingProviders.map((provider) => (
          <Item key={provider} provider={provider} onClick={() => onSelect(provider)} />
        ))}

        {mailingProviders.map((provider) => (
          <Item key={provider} provider={provider} onClick={() => onSelect(provider)} />
        ))}
      </CardContent>
    </Card>
  );
}

function Item({ provider, onClick }: { provider: Provider; onClick: () => void }) {
  return (
    <div
      className="hover:bg-accent bg-background flex cursor-pointer items-center space-x-4 rounded-md border p-3 transition-colors"
      onClick={onClick}
    >
      <img alt={ProviderNames[provider]} className="h-7 w-7 rounded-md" src={ProviderIcons[provider]} />
      <p className="text-sm font-medium leading-none">{ProviderNames[provider]}</p>
    </div>
  );
}
